import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { includeWishList, loadWishListAcquisitions } from '../lib/wishList'
import { subscribeCollectionAdded } from '../lib/collectionEvents'
import { dismissIncomingShare, dismissedShareIds, hydrateIncomingFragranceShares, loadIncomingShareRows } from '../lib/sharedRouting'
import { useFeatureFlag } from './useFeatureFlag'

const empty = { rows: [], displayRows: [], acquired: [], enabled: false, loading: false, error: '', available: false }
const WishListContext = createContext(empty)
export const useWishList = () => useContext(WishListContext)

export function WishListProvider({ userId, children }) {
  const sharedRoutingEnabled = useFeatureFlag('shared_routing')
  const [state, setState] = useState(empty)
  const request = useRef(0)
  const refresh = useCallback(async () => {
    const version = ++request.current
    if (!userId) { setState(empty); return }
    setState(current => ({ ...current, loading: true }))
    try {
      const [wishes, wears, owned, acquired, incoming] = await Promise.all([
        supabase.from('wish_list').select('*,catalog:catalog_id(id,name,house,concentration,card_image,top_notes,mid_notes,base_notes,accord_tags,scent_family,layering_role,best_layer_pair_type)').eq('user_id', userId).order('added_at', { ascending: false }),
        supabase.from('wear_logs').select('id', { count: 'exact', head: true }).eq('user_id', userId),
        supabase.from('fragrances').select('catalog_id').eq('user_id', userId).is('deleted_at', null),
        loadWishListAcquisitions(supabase, userId),
        sharedRoutingEnabled ? loadIncomingShareRows(supabase, userId) : [],
      ])
      if (wishes.error || wears.error || owned.error) throw wishes.error || wears.error || owned.error
      if (version !== request.current) return
      const ownedIds = new Set((owned.data || []).map(row => row.catalog_id))
      const rows = wishes.data.filter(row => row.catalog_id && !ownedIds.has(row.catalog_id))
      const dismissed = dismissedShareIds(userId)
      const sharedRows = sharedRoutingEnabled
        ? (await hydrateIncomingFragranceShares(supabase, incoming))
          .filter(row => !dismissed.has(String(row.shared_id)) && (!row.catalog_id || !ownedIds.has(row.catalog_id)))
        : []
      if (version !== request.current) return
      const preference = wishes.data.find(row => row.catalog_id === null)?.include_wish_list
      setState({ rows, displayRows: sharedRoutingEnabled ? [...sharedRows, ...rows] : rows, acquired,
        enabled: includeWishList(preference, wears.count || 0), loading: false, error: '', available: true })
    } catch (err) {
      if (version !== request.current) return
      console.warn('wish list load failed', err?.message || err)
      setState({ ...empty, error: 'Wish list is unavailable. Please try again later.' })
    }
  }, [userId, sharedRoutingEnabled])
  useEffect(() => { setState(empty); refresh(); return () => { request.current++ } }, [refresh])
  useEffect(() => subscribeCollectionAdded(rows => {
    const ids = new Set(rows.filter(row => row.user_id === userId).map(row => row.catalog_id))
    setState(current => ({
      ...current,
      rows: current.rows.filter(row => !ids.has(row.catalog_id)),
      displayRows: current.displayRows.filter(row => !ids.has(row.catalog_id)),
    }))
    // The collection trigger records the move server-side; pick up the ledger row.
    if (userId) loadWishListAcquisitions(supabase, userId).then(acquired => setState(current => ({ ...current, acquired })))
  }), [userId])
  const remove = async catalogId => {
    const { error } = await supabase.from('wish_list').delete().eq('user_id', userId).eq('catalog_id', catalogId)
    if (error) throw new Error('Could not remove this wish. Please try again.')
    setState(current => ({
      ...current,
      rows: current.rows.filter(row => row.catalog_id !== catalogId),
      displayRows: current.displayRows.filter(row => row.is_shared || row.catalog_id !== catalogId),
    }))
  }
  const dismissShared = sharedId => {
    dismissIncomingShare(userId, sharedId)
    setState(current => ({ ...current, displayRows: current.displayRows.filter(row => row.shared_id !== sharedId) }))
  }
  const add = async (catalogId, source) => {
    const { error } = await supabase.from('wish_list').upsert({ user_id: userId, catalog_id: catalogId, source }, { onConflict: 'user_id,catalog_id', ignoreDuplicates: true })
    if (error) throw new Error('Could not save this wish. Check that it is not already in your collection, then try again.')
    await refresh()
  }
  const setEnabled = async enabled => {
    const { error } = await supabase.from('wish_list').upsert({ user_id: userId, catalog_id: null, source: 'settings', include_wish_list: enabled }, { onConflict: 'user_id,catalog_id' })
    if (error) throw new Error('Could not save your preference. Please try again.')
    setState(current => ({ ...current, enabled }))
  }
  return <WishListContext.Provider value={{ ...state, add, remove, dismissShared, setEnabled, refresh }}>{children}</WishListContext.Provider>
}
